import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Table,
  Button,
  Card,
  Modal,
  Form,
  Input,
  Space,
  Popconfirm,
  Typography,
  Spin,
  Select,
  DatePicker,
  InputNumber,
  Row,
  Col,
  Tag,
  Statistic,
  Empty,
  message,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  FilterOutlined,
  DownloadOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import { observer } from "mobx-react-lite";
import dayjs from "dayjs";
import { debounce } from "lodash";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";
import { useStore } from "../../../stores/root-store-context";
import { FinancialTransaction } from "../../../api/FinanceApiClient";
import { EntityVersion } from "../../../stores/FinanceStore";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;

type RangeValue = [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;

const TransactionManagement = observer(() => {
  const { t } = useTranslation();
  const { financeStore } = useStore();
  const main = financeStore.mainCurrency;

  const [searchText, setSearchText] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState<number | null>(null);
  const [selectedOperationType, setSelectedOperationType] = useState<number | null>(null);
  const [selectedPaymentType, setSelectedPaymentType] = useState<number | null>(null);
  const [selectedOperationCategory, setSelectedOperationCategory] = useState<number | null>(null);
  const [dateRange, setDateRange] = useState<RangeValue>([
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ]);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<FinancialTransaction | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [rates, setRates] = useState<Record<string, number>>({});
  const [versionsVisible, setVersionsVisible] = useState(false);
  const [currentVersions, setCurrentVersions] = useState<EntityVersion[]>([]);
  const [currentTxId, setCurrentTxId] = useState<number | null>(null);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50", "100"],
  });

  const [form] = Form.useForm();

  useEffect(() => {
    (async () => {
      await financeStore.getCurrencies();
      await financeStore.getPaymentTypes();
      await financeStore.getOperationCategories();
      await financeStore.getOperationTypes();

      financeStore.pageSize = pagination.pageSize;
      financeStore.currentPage = pagination.current;
      financeStore.dateRange = dateRange;

      await loadTransactions(
        pagination.current,
        pagination.pageSize,
        searchText,
        selectedCurrency,
        selectedOperationType,
        selectedPaymentType,
        dateRange,
        selectedOperationCategory
      );
    })();
  }, []);

  const toMain = (amt: number, short: string) => {
    if (!main || !short || !rates) return amt;
    if (!main.shortName) return amt;
    if (short.toLowerCase() === main.shortName.toLowerCase()) return amt;
    const r = rates[short.toLowerCase()];
    return r ? amt / r : amt;
  };

  const loadTransactions = async (
    page = pagination.current,
    size = pagination.pageSize,
    currentSearchText = searchText,
    currentCurrency = selectedCurrency,
    currentOperationType = selectedOperationType,
    currentPaymentType = selectedPaymentType,
    currentDateRange = dateRange,
    currentOperationCategory = selectedOperationCategory
  ) => {
    financeStore.searchText = currentSearchText;
    financeStore.selectedCurrency = currentCurrency;
    financeStore.selectedOperationType = currentOperationType;
    financeStore.selectedPaymentType = currentPaymentType;
    financeStore.dateRange = currentDateRange;
    financeStore.selectedOperationCategory = currentOperationCategory;

    financeStore.currentPage = page;
    financeStore.pageSize = size;

    const res = await financeStore.getPaginatedTransactions(page, size);
    if (res) {
      setPagination((p) => ({
        ...p,
        current: page,
        pageSize: size,
        total: financeStore.totalTransactions,
      }));
    }
  };

  const handleTableChange = (p: any) => {
    const sizeChanged = p.pageSize !== pagination.pageSize;
    const newPage = sizeChanged ? 1 : p.current;
    const newSize = p.pageSize;

    setPagination((prev) => ({ ...prev, current: newPage, pageSize: newSize }));
    loadTransactions(
      newPage,
      newSize,
      searchText,
      selectedCurrency,
      selectedOperationType,
      selectedPaymentType,
      dateRange,
      selectedOperationCategory
    );
  };

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setSearchText(value);
      const newSize = pagination.pageSize;
      setPagination((p) => ({ ...p, current: 1 }));
      loadTransactions(
        1,
        newSize,
        value,
        selectedCurrency,
        selectedOperationType,
        selectedPaymentType,
        dateRange,
        selectedOperationCategory
      );
    }, 500),
    [
      selectedCurrency,
      selectedOperationType,
      selectedPaymentType,
      selectedOperationCategory,
      dateRange,
      pagination.pageSize,
    ]
  );

  const handleFilterChange = (
    type: "currency" | "operationType" | "paymentType" | "dateRange" | "operationCategory",
    value: any
  ) => {
    let newCategoryValue = selectedOperationCategory;
    let newCurrencyValue = selectedCurrency;
    let newOperationTypeValue = selectedOperationType;
    let newPaymentTypeValue = selectedPaymentType;
    let newDateRangeValue = dateRange;

    switch (type) {
      case "currency":
        newCurrencyValue = value;
        setSelectedCurrency(value);
        break;
      case "operationType":
        newOperationTypeValue = value;
        setSelectedOperationType(value);
        break;
      case "paymentType":
        newPaymentTypeValue = value;
        setSelectedPaymentType(value);
        break;
      case "dateRange":
        newDateRangeValue = value;
        setDateRange(value);
        break;
      case "operationCategory":
        newCategoryValue = value;
        setSelectedOperationCategory(value);
        break;
    }

    setPagination((p) => ({ ...p, current: 1 }));

    loadTransactions(
      1,
      pagination.pageSize,
      searchText,
      newCurrencyValue,
      newOperationTypeValue,
      newPaymentTypeValue,
      newDateRangeValue,
      newCategoryValue
    );
  };

  const resetFilters = () => {
    const newDate: RangeValue = [dayjs().startOf("month"), dayjs().endOf("month")];
    setSearchText("");
    setSelectedCurrency(null);
    setSelectedOperationType(null);
    setSelectedPaymentType(null);
    setSelectedOperationCategory(null);
    setDateRange(newDate);
    setPagination((p) => ({ ...p, current: 1 }));
    loadTransactions(1, pagination.pageSize, "", null, null, null, newDate, null);
  };

  useEffect(() => {
    if (!main?.shortName) return;
    fetch(
      `https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/${main.shortName.toLowerCase()}.json`
    )
      .then((r) => r.json())
      .then((j) => {
        if (j && j[main.shortName.toLowerCase()]) {
          setRates(j[main.shortName.toLowerCase()]);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch currency rates:", err);
        setRates({});
      });
  }, [main]);

  const sortedCategories = useMemo(
    () =>
      [...financeStore.operationCategories].sort((a, b) =>
        t(a.name).localeCompare(t(b.name), undefined, { sensitivity: "base" })
      ),
    [financeStore.operationCategories, t]
  );

  const sortedCurrencies = useMemo(
    () =>
      [...financeStore.currencies].sort((a, b) =>
        (a.shortName || "").localeCompare(b.shortName || "", undefined, { sensitivity: "base" })
      ),
    [financeStore.currencies]
  );

  const sortedOperationTypes = useMemo(
    () =>
      [...financeStore.operationTypes].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
      ),
    [financeStore.operationTypes]
  );

  const sortedPaymentTypes = useMemo(
    () =>
      [...financeStore.paymentTypes].sort((a, b) =>
        t(a.name).localeCompare(t(b.name), undefined, { sensitivity: "base" })
      ),
    [financeStore.paymentTypes, t]
  );

  const columns = [
    {
      title: t("Date"),
      dataIndex: "date",
      key: "date",
      width: "10%",
      render: (v: string) => (v ? dayjs(v).format("YYYY-MM-DD") : "-"),
      sorter: (a: FinancialTransaction, b: FinancialTransaction) =>
        new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime(),
    },
    {
      title: t("Name"),
      dataIndex: "name",
      key: "name",
      width: "15%",
      render: (v: string) => v || "-",
    },
    {
      title: t("Description"),
      dataIndex: "description",
      key: "description",
      width: "20%",
      ellipsis: true,
      render: (v: string) => v || "-",
    },
    {
      title: t("Amount"),
      key: "amount",
      width: "10%",
      render: (_: any, r: FinancialTransaction) => {
        const op = financeStore.operationTypes.find((o) => o.id === r.operationTypeId);
        const cat = op ? financeStore.operationCategories.find((c) => c.id === op.operationCategoryId) : null;
        const cur = financeStore.currencies.find((c) => c.id === r.currencyId);
        const isInc = cat?.id === 1;
        const symbol = cur?.symbol || "";
        const amount = r.amount || 0;

        return (
          <span style={{ color: isInc ? "#3f8600" : "#cf1322", fontWeight: "bold" }}>
            {isInc ? "+" : "-"}
            {symbol}
            {amount.toFixed(2)}
          </span>
        );
      },
      sorter: (a: FinancialTransaction, b: FinancialTransaction) => (a.amount || 0) - (b.amount || 0),
    },
    {
      title: t("Currency"),
      key: "currency",
      width: "7%",
      render: (_: any, r: FinancialTransaction) =>
        financeStore.currencies.find((c) => c.id === r.currencyId)?.shortName || "-",
    },
    {
      title: t("Type"),
      key: "type",
      width: "8%",
      render: (_: any, r: FinancialTransaction) => {
        const op = financeStore.operationTypes.find((o) => o.id === r.operationTypeId);
        const cat = op ? financeStore.operationCategories.find((c) => c.id === op.operationCategoryId) : null;
        const isInc = cat?.id === 1;
        return <Tag color={isInc ? "green" : "red"}>{isInc ? t("Income") : t("Expense")}</Tag>;
      },
    },
    {
      title: t("Operation"),
      key: "operation",
      width: "10%",
      render: (_: any, r: FinancialTransaction) =>
        financeStore.operationTypes.find((o) => o.id === r.operationTypeId)?.name || "-",
    },
    {
      title: t("Payment"),
      key: "payment",
      width: "12%",
      render: (_: any, r: FinancialTransaction) => {
        const paymentName = financeStore.paymentTypes.find((p) => p.id === r.paymentTypeId)?.name;
        return paymentName ? t(paymentName) : "-";
      },
    },
    {
      title: t("Actions"),
      key: "actions",
      width: "10%",
      render: (_: any, r: FinancialTransaction) => (
        <Space size="small">
          <Button icon={<HistoryOutlined />} size="small" onClick={() => openVersionsModal(r)} />
          <Button type="primary" icon={<EditOutlined />} size="small" onClick={() => openEditModal(r)} />
          <Popconfirm
            title={t("DeleteTransactionConfirm")}
            onConfirm={() => handleDelete(r.id)}
            okText={t("Yes")}
            cancelText={t("No")}
          >
            <Button type="primary" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const exportToExcel = async () => {
    financeStore.setLoading(true);
    try {
      const exportData = await financeStore.getTransactionsForExport(
        searchText,
        selectedCurrency,
        selectedOperationType,
        selectedPaymentType,
        dateRange,
        selectedOperationCategory
      );

      const rows = exportData.map((r: any) => {
        const cur = financeStore.currencies.find((c) => c.id === r.currencyId);
        const op = financeStore.operationTypes.find((o) => o.id === r.operationTypeId);
        const cat = op ? financeStore.operationCategories.find((ca) => ca.id === op.operationCategoryId) : null;
        const pay = financeStore.paymentTypes.find((p) => p.id === r.paymentTypeId);
        const isInc = cat?.id === 1;

        const mainVal = cur?.shortName ? toMain(r.amount || 0, cur.shortName) : r.amount || 0;

        return {
          Date: r.date || "",
          Name: r.name || "",
          Description: r.description || "",
          Amount: (isInc ? "+" : "-") + (r.amount || 0).toFixed(2),
          [`Amount (${main?.shortName || "Main"})`]: mainVal.toFixed(2),
          Currency: cur?.shortName || "-",
          Type: isInc ? t("Income") : t("Expense"),
          Operation: op?.name || "-",
          Payment: pay?.name || "-",
        };
      });

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Transactions");
      XLSX.writeFile(wb, "transactions.xlsx");
    } catch (error) {
      console.error("Export failed:", error);
      message.error(t("FailedToExportData"));
    } finally {
      financeStore.setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingTransaction(null);
    setSelectedCategoryId(null);
    form.resetFields();
    form.setFieldsValue({ date: dayjs() });
    setIsModalVisible(true);
  };

  const openEditModal = (rec: FinancialTransaction) => {
    setEditingTransaction(rec);
    const op = financeStore.operationTypes.find((o) => o.id === rec.operationTypeId);
    setSelectedCategoryId(op?.operationCategoryId ?? null);
    form.setFieldsValue({
      ...rec,
      date: rec.date ? dayjs(rec.date) : dayjs(),
      operationCategoryId: op?.operationCategoryId,
    });
    setIsModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const v = await form.validateFields();
      const data = { ...v, date: v.date.format("YYYY-MM-DD") };

      if (editingTransaction) {
        await financeStore.updateTransaction(editingTransaction.id, data);
      } else {
        await financeStore.addTransaction(data);
      }

      setIsModalVisible(false);

      refreshOverview();

      loadTransactions(
        pagination.current,
        pagination.pageSize,
        searchText,
        selectedCurrency,
        selectedOperationType,
        selectedPaymentType,
        dateRange,
        selectedOperationCategory
      );
    } catch (error) {
      console.error("Failed to save transaction:", error);
      message.error(t("FailedToSaveTransaction"));
    }
  };

  const openVersionsModal = async (rec: FinancialTransaction) => {
    try {
      const versions = await financeStore.getTransactionVersions(rec.id);
      setCurrentVersions(versions);
      setCurrentTxId(rec.id);
      setVersionsVisible(true);
    } catch (error) {
      console.error("Failed to load versions:", error);
      message.error(t("FailedToLoadVersionHistory"));
    }
  };

  const handleRollback = async (versionId: number) => {
    if (!currentTxId) return;
    try {
      await financeStore.rollbackTransaction(currentTxId, versionId);
      setVersionsVisible(false);
      loadTransactions(
        pagination.current,
        pagination.pageSize,
        searchText,
        selectedCurrency,
        selectedOperationType,
        selectedPaymentType,
        dateRange,
        selectedOperationCategory
      );
      message.success(t("RollbackSuccessful"));
    } catch (error) {
      console.error("Failed to rollback:", error);
      message.error(t("FailedToRollback"));
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await financeStore.deleteTransaction(id);
      const isLastItemOnPage = financeStore.transactions.length === 1;
      if (isLastItemOnPage && pagination.current > 1) {
        const newPage = pagination.current - 1;
        setPagination((prev) => ({ ...prev, current: newPage }));
        loadTransactions(
          newPage,
          pagination.pageSize,
          searchText,
          selectedCurrency,
          selectedOperationType,
          selectedPaymentType,
          dateRange,
          selectedOperationCategory
        );
      } else {
        loadTransactions(
          pagination.current,
          pagination.pageSize,
          searchText,
          selectedCurrency,
          selectedOperationType,
          selectedPaymentType,
          dateRange,
          selectedOperationCategory
        );
      }

      refreshOverview();
      message.success(t("TransactionDeleted"));
    } catch (error) {
      console.error("Failed to delete transaction:", error);
      message.error(t("FailedToDeleteTransaction"));
    }
  };

  const refreshOverview = () => {
    if (dateRange?.[0] && dateRange?.[1]) {
      financeStore.getOverviewTransactions(
        dateRange[0].format("YYYY-MM-DD"),
        dateRange[1].format("YYYY-MM-DD"),
        selectedCurrency
      );
      financeStore.getOverviewSummary(
        dateRange[0].format("YYYY-MM-DD"),
        dateRange[1].format("YYYY-MM-DD"),
        selectedCurrency
      );
    }
  };

  if (financeStore.isLoading)
    return (
      <div
        style={{
          height: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Spin size="large" />
      </div>
    );

  if (!financeStore.currencies || financeStore.currencies.length === 0)
    return (
      <Card style={{ textAlign: "center" }}>
        <Empty description={t("PleaseAddCurrencyFirst")} />
      </Card>
    );

  if (!main)
    return (
      <Card style={{ textAlign: "center" }}>
        <Empty description={t("PleaseSetMainCurrency")} />
      </Card>
    );

  const statsSymbol = selectedCurrency
    ? financeStore.currencies.find((c) => c.id === selectedCurrency)?.symbol || ""
    : main?.symbol || "";

  return (
    <div style={{ padding: 10 }}>
      <Card>
        <Row gutter={[16, 16]} wrap style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={6} lg={4} xl={4}>
            <Input
              placeholder={t("SearchTransactions")}
              prefix={<SearchOutlined />}
              onChange={(e) => debouncedSearch(e.target.value)}
              value={searchText}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6} lg={5} xl={5}>
            <RangePicker
              value={dateRange}
              onChange={(dates) => handleFilterChange("dateRange", dates)}
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={24} sm={12} md={6} lg={3} xl={3}>
            <Select
              placeholder={t("SelectCategory")}
              value={selectedOperationCategory}
              onChange={(val) => handleFilterChange("operationCategory", val == null ? null : Number(val))}
              style={{ width: "100%" }}
              allowClear
            >
              {sortedCategories.map((c) => (
                <Option key={c.id} value={c.id}>
                  {t(c.name)}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6} lg={3} xl={3}>
            <Select
              placeholder={t("SelectCurrency")}
              value={selectedCurrency}
              onChange={(value) => handleFilterChange("currency", value)}
              style={{ width: "100%" }}
              allowClear
            >
              {sortedCurrencies.map((c) => (
                <Option key={c.id} value={c.id}>
                  {c.shortName} ({c.symbol})
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6} lg={4} xl={4}>
            <Select
              placeholder={t("SelectOperationType")}
              value={selectedOperationType}
              onChange={(value) => handleFilterChange("operationType", value)}
              style={{ width: "100%" }}
              allowClear
            >
              {sortedOperationTypes.map((o) => (
                <Option key={o.id} value={o.id}>
                  {o.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6} lg={4} xl={4}>
            <Select
              placeholder={t("SelectPaymentType")}
              value={selectedPaymentType}
              onChange={(value) => handleFilterChange("paymentType", value)}
              style={{ width: "100%" }}
              allowClear
            >
              {sortedPaymentTypes.map((p) => (
                <Option key={p.id} value={p.id}>
                  {t(p.name)}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={24} md={12} lg={5} xl={4} style={{ display: "flex" }}>
            <Space style={{ display: "flex" }}>
              <Button icon={<FilterOutlined />} onClick={resetFilters}>
                {t("Reset")}
              </Button>
              <Button icon={<DownloadOutlined />} onClick={exportToExcel}>
                {t("Export")}
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>
                {t("Add")}
              </Button>
            </Space>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 12 }}>
          <Col xs={24} sm={8}>
            <Statistic
              title={t("TotalIncome")}
              value={financeStore.summaryTotals?.income || 0}
              precision={2}
              valueStyle={{ color: "#3f8600" }}
              prefix={<ArrowUpOutlined />}
              suffix={statsSymbol}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title={t("TotalExpense")}
              value={financeStore.summaryTotals?.expense || 0}
              precision={2}
              valueStyle={{ color: "#cf1322" }}
              prefix={<ArrowDownOutlined />}
              suffix={statsSymbol}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title={t("Balance")}
              value={financeStore.summaryTotals?.balance || 0}
              precision={2}
              valueStyle={{
                color: (financeStore.summaryTotals?.balance || 0) >= 0 ? "#3f8600" : "#cf1322",
              }}
              suffix={statsSymbol}
            />
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={financeStore.transactions || []}
          rowKey="id"
          loading={financeStore.isLoading}
          onChange={handleTableChange}
          pagination={pagination}
          bordered
        />

        <Modal
          title={editingTransaction ? t("EditTransaction") : t("AddTransaction")}
          open={isModalVisible}
          onOk={handleSave}
          onCancel={() => setIsModalVisible(false)}
          okText={t("OK")}
          cancelText={t("Cancel")}
          destroyOnClose
          width={700}
        >
          <Form form={form} layout="vertical">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="operationCategoryId"
                  label={t("Category")}
                  rules={[{ required: true, message: t("PleaseSelectCategory") }]}
                >
                  <Select
                    placeholder={t("SelectCategory")}
                    value={selectedCategoryId}
                    onChange={(v) => {
                      setSelectedCategoryId(v);
                      form.setFieldsValue({ operationTypeId: undefined });
                    }}
                  >
                    {sortedCategories.map((c) => (
                      <Option key={c.id} value={c.id}>
                        {t(c.name)}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="operationTypeId"
                  label={t("OperationType")}
                  rules={[{ required: true, message: t("PleaseSelectOperationType") }]}
                >
                  <Select disabled={!selectedCategoryId} placeholder={t("SelectOperationType")}>
                    {sortedOperationTypes
                      .filter((o) => o.operationCategoryId === selectedCategoryId)
                      .map((o) => (
                        <Option key={o.id} value={o.id}>
                          {o.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label={t("Name")}
                  rules={[{ required: true, message: t("PleaseEnterTransactionName") }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="date"
                  label={t("Date")}
                  rules={[{ required: true, message: t("PleaseSelectDate") }]}
                >
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="description"
              label={t("Description")}
              rules={[{ required: true, message: t("PleaseEnterTransactionDescription") }]}
            >
              <Input.TextArea rows={2} />
            </Form.Item>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="amount"
                  label={t("Amount")}
                  rules={[{ required: true, message: t("PleaseEnterAmount") }]}
                >
                  <InputNumber style={{ width: "100%" }} min={0} precision={2} placeholder="0.00" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="currencyId"
                  label={t("Currency")}
                  rules={[{ required: true, message: t("PleaseSelectCurrency") }]}
                >
                  <Select placeholder={t("SelectCurrency")}>
                    {sortedCurrencies.map((c) => (
                      <Option key={c.id} value={c.id}>
                        {c.shortName} ({c.symbol})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="paymentTypeId"
                  label={t("PaymentType")}
                  rules={[{ required: true, message: t("PleaseSelectPaymentType") }]}
                >
                  <Select placeholder={t("SelectPaymentType")}>
                    {sortedPaymentTypes.map((p) => (
                      <Option key={p.id} value={p.id}>
                        {t(p.name)}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>

        <Modal
          title={t("Versions")}
          open={versionsVisible}
          onCancel={() => setVersionsVisible(false)}
          footer={null}
          width={600}
        >
          <Table
            dataSource={currentVersions}
            rowKey="id"
            pagination={false}
            columns={[
              { title: t("Tag"), dataIndex: "versionTag", key: "tag" },
              {
                title: t("ChangedAt"),
                dataIndex: "createdDate",
                key: "createdDate",
                render: (v: string) => (v ? dayjs(v).format("YYYY-MM-DD HH:mm") : "-"),
              },
              {
                title: t("ChangedBy"),
                key: "user",
                render: (_: any, v: EntityVersion) =>
                  v.user?.firstName ? `${v.user.firstName} ${v.user.lastName ?? ""}`.trim() : "-",
              },
              {
                title: t("Actions"),
                key: "act",
                render: (_: any, v: EntityVersion) => (
                  <Popconfirm
                    title={t("RollbackConfirm")}
                    onConfirm={() => handleRollback(v.id)}
                    okText={t("Yes")}
                    cancelText={t("No")}
                  >
                    <Button type="primary">{t("Rollback")}</Button>
                  </Popconfirm>
                ),
              },
            ]}
          />
        </Modal>
      </Card>
    </div>
  );
});

export default TransactionManagement;
